import { message, Upload } from "antd";
import { UploadIcon } from "../../Icons/uploadIcon";
import "./style.scss";
import { usePostData } from "../../Network/usePostData";
import { Loading } from "../Loading";
import { useTranslation } from "react-i18next";

const { Dragger } = Upload;

interface UploaderProps {
  onChange?: (url: string) => void;
  multiple?: boolean;
}

export const Uploader = ({ onChange, multiple }: UploaderProps) => {
  const { t } = useTranslation();
  const { mutateAsync, isPending } = usePostData({
    url: "media/image",
  });

  const getBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const handleUpload = async (file: File) => {
    try {
      const base64 = await getBase64(file);
      const response = (await mutateAsync({
        name: file.name,
        base64: base64.replace(/^data:image\/\w+;base64,/, ""),
      })) as any;

      if (response.data) {
        onChange?.(response.data?.url || response.data);
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      message.error(`${file.name} ${t("uploader.uploadFailed")}`);
    }
  };

  const props = {
    name: "file",
    multiple: false,
    accept: ".png,.jpeg,.jpg",
    beforeUpload: (file: File) => {
      const isImage = file.type === "image/png" || file.type === "image/jpeg";
      if (!isImage) {
        message.error(t("uploader.invalidFormat"));
      }
      handleUpload(file);
      return false; // Prevent automatic upload
    },
  };

  if (isPending)
    return (
      <div className="ant-upload-wrapper">
        <div
          className="ant-upload ant-upload-drag"
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Loading />
        </div>
      </div>
    );

  return (
    <Dragger {...props} multiple={multiple}>
      <p className="ant-upload-drag-icon">
        <UploadIcon />
      </p>
      <p className="uploader-text-helper">
        {t("uploader.dropOrBrowse")} <a>{t("uploader.browse")}</a>{" "}
        {t("uploader.supportedFormats")}
      </p>
    </Dragger>
  );
};
